import { useCallback } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { DismissAllCommentsForChannelsMutation } from './useDismissAll'
import type { DismissAllCommentsForChannels } from './useDismissAll'

export type UseDismissAllCommentsForChannelsResult = {
  dismissAllCommentsForChannels: DismissAllCommentsForChannels
  loading: boolean
}

/**
 * Hook to dismiss all comments for channels
 *
 * @returns {UseDismissAllCommentsForChannelsResult} - The dismissAllCommentsForChannels function
 *
 * @example
 *
 * const dismissAllCommentsForChannels = useDismissAllCommentsForChannels()
 *
 * const handleDismissAllCommentsForChannels = async () => {
 *  await dismissAllCommentsForChannels(channelId)
 * }
 *
 * // With date filter
 * const handleDismissOlderComments = async () => {
 *  const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000
 *  const oneMonthAgo = new Date(Date.now() - ONE_MONTH_MS).toISOString()
 *  await dismissAllCommentsForChannels(channelId, oneMonthAgo)
 * }
 *
 */
export const useDismissAllCommentsForChannels =
  (): DismissAllCommentsForChannels => {
    const [dismissAllMutation] = useTypedMutation(
      DismissAllCommentsForChannelsMutation,
      (data) => data.dismissAllCommentsForChannels,
      {
        successTypename: 'DismissAllCommentsForChannelsSuccess',
        refetchQueries: ['Comments'],
      },
    )

    const dismissAllCommentsForChannels = useCallback(
      async (channelIds: string | string[], toDate?: string): Promise<void> => {
        const channelIdArray = Array.isArray(channelIds)
          ? channelIds
          : [channelIds]

        if (channelIdArray.length === 0) {
          throw new Error('At least one channel id is required')
        }

        try {
          const result = await dismissAllMutation({
            variables: {
              input: {
                channelIds: channelIdArray,
                ...(toDate ? { toDate } : {}),
              },
            },
          })

          if (result.success && result.data) {
            const channels = result.data.channels
            if (channels && Array.isArray(channels)) {
              const successfulChannels = channels.filter(
                (channel) => channel.status === 'success',
              )

              if (successfulChannels.length === 0) {
                toast.success("We couldn't find any comment to resolve")
              } else {
                // Determine toast message based on number of channels and date filter
                const isSingleChannel = channelIdArray.length === 1
                const hasDateFilter = !!toDate

                let message: string
                if (isSingleChannel) {
                  message = hasDateFilter
                    ? 'All comments older than 1 month were resolved.'
                    : 'All comments were resolved.'
                } else {
                  message = hasDateFilter
                    ? 'All comments older than 1 month from selected channels were resolved.'
                    : 'All comments from selected channels were resolved.'
                }

                toast.success(message)
              }
            } else {
              toast.success("We couldn't find any comment to resolve")
            }
          } else {
            toast.error('An error occurred while resolving the comments')
          }
        } catch {
          toast.error('An error occurred while resolving the comments')
        }
      },
      [dismissAllMutation],
    )

    return dismissAllCommentsForChannels
  }
